import { useRef, useState } from "react";
import {
  CloudArrowUp,
  Camera,
  ShieldCheck,
  Warning,
  XCircle,
  Question,
  Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Verify() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const analyse = async () => {
    if (!file) {
      toast.error("Upload an image first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/hallmark/verify", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not analyse image");
    } finally {
      setLoading(false);
    }
  };

  const verdictTone = (v) => {
    if (!v) return { bg: "bg-[#F5EFE6]", text: "text-[#0A1128]", Icon: Question };
    if (v.includes("GENUINE"))
      return { bg: "bg-[#16A34A]/10", text: "text-[#16A34A]", Icon: ShieldCheck };
    if (v.includes("NOT HALLMARKED") || v.includes("SUSPICIOUS"))
      return { bg: "bg-[#DC2626]/10", text: "text-[#DC2626]", Icon: XCircle };
    return { bg: "bg-[#EAB308]/10", text: "text-[#B45309]", Icon: Warning };
  };

  return (
    <div data-testid="verify-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
          Hallmark Verifier
        </p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128] tracking-tight">
          Verify your jewellery in seconds.
        </h1>
        <p className="mt-3 text-[#4A5568] max-w-2xl">
          Upload a clear photo of your jewellery or its hallmark — Mine AI
          decodes the BIS code and tells you if it's genuine.
        </p>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {/* Upload */}
          <div
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className="mc-card p-6 sm:p-8 border-dashed"
            style={{ borderStyle: "dashed", borderColor: "rgba(212,175,55,0.5)" }}
            data-testid="verify-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="verify-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {preview ? (
              <div className="space-y-4">
                <img
                  src={preview}
                  alt="preview"
                  className="w-full max-h-96 object-contain rounded-lg border border-[#E8DFD1] bg-[#F5EFE6]/50"
                  data-testid="verify-preview"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="verify-change-btn"
                    onClick={() => inputRef.current?.click()}
                    className="flex-1 py-2.5 rounded-md border border-[#E8DFD1] text-sm hover:border-[#3B5BDB] smooth"
                  >
                    Change image
                  </button>
                  <button
                    data-testid="verify-analyse-btn"
                    onClick={analyse}
                    disabled={loading}
                    className="flex-1 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
                  >
                    {loading ? "Analysing..." : "Analyse with Mine AI"}
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center text-center py-10"
                data-testid="verify-empty"
              >
                <CloudArrowUp size={56} weight="duotone" className="text-[#3B5BDB]" />
                <p className="mt-3 font-display text-2xl text-[#0A1128]">
                  Drop your jewellery photo here
                </p>
                <p className="text-sm text-[#4A5568] mt-1">
                  or click below to upload (jpg, png, webp)
                </p>
                <div className="mt-5 flex gap-2">
                  <button
                    data-testid="verify-upload-btn"
                    onClick={() => inputRef.current?.click()}
                    className="px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
                  >
                    Upload image
                  </button>
                  <button
                    onClick={() => inputRef.current?.click()}
                    className="px-5 py-2.5 rounded-md border border-[#E8DFD1] text-sm hover:border-[#3B5BDB] smooth flex items-center gap-2"
                  >
                    <Camera size={16} weight="duotone" /> Take photo
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Result */}
          <div className="mc-card p-6 sm:p-8" data-testid="verify-result-card">
            <h3 className="font-display text-2xl text-[#0A1128]">
              Mine AI's verdict
            </h3>
            {!result && !loading && (
              <p className="mt-3 text-sm text-[#4A5568]">
                Upload a photo to see verdict, decoded BIS hallmark, and honest
                buying advice here.
              </p>
            )}
            {loading && (
              <div className="mt-5 flex items-center gap-3 text-[#4A5568]">
                <Sparkle size={20} weight="duotone" className="text-[#D4AF37] animate-pulse" />
                Reading the hallmark...
              </div>
            )}
            {result && (() => {
              const t = verdictTone(result.verdict);
              return (
                <div className="mt-5">
                  <div
                    data-testid="verdict-badge"
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${t.bg} ${t.text} text-sm font-medium`}
                  >
                    <t.Icon size={18} weight="fill" />
                    {result.verdict}
                  </div>
                  <pre
                    data-testid="verdict-analysis"
                    className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-[#0A1128]/90 font-body bg-[#F5EFE6]/40 p-4 rounded-lg border border-[#E8DFD1]"
                  >
                    {result.analysis}
                  </pre>
                </div>
              );
            })()}
          </div>
        </div>

        {/* BIS education */}
        <div className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            BIS Hallmark 101
          </p>
          <h2 className="font-display text-3xl mt-2 text-[#0A1128]">
            What every gold buyer should know
          </h2>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                t: "What is BIS?",
                d: "Bureau of Indian Standards certifies gold purity. Look for the triangular BIS logo on every piece.",
              },
              {
                t: "916 / 750 / 585",
                d: "916 = 22K (91.6% pure). 750 = 18K. 585 = 14K. The number tells you exactly how much gold is in it.",
              },
              {
                t: "Where to find it",
                d: "Hallmarks are stamped inside rings, on chain clasps, or near the lock of bangles. A magnifier helps.",
              },
              {
                t: "Why it matters",
                d: "Without a hallmark, you have no proof of purity. Never pay 22K prices for un-hallmarked gold.",
              },
            ].map((c) => (
              <div
                key={c.t}
                className="mc-card lift smooth p-5"
                data-testid={`bis-card-${c.t}`}
              >
                <ShieldCheck size={22} weight="duotone" className="text-[#D4AF37]" />
                <p className="font-display text-xl mt-3">{c.t}</p>
                <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
